// Centralized error handling system

export class AppError extends Error {
  public readonly statusCode: number;
  public readonly code: string;
  public readonly isOperational: boolean;
  public readonly details?: unknown;

  constructor(
    message: string,
    statusCode: number = 500,
    code: string = 'INTERNAL_ERROR',
    isOperational: boolean = true,
    details?: unknown
  ) {
    super(message);
    this.name = 'AppError';
    this.statusCode = statusCode;
    this.code = code;
    this.isOperational = isOperational;
    this.details = details;

    // Capture stack trace
    Error.captureStackTrace(this, this.constructor);
  }
}

// Specific error types
export class AuthenticationError extends AppError {
  constructor(message: string = 'Authentication required', details?: unknown) {
    super(message, 401, 'AUTH_REQUIRED', true, details);
    this.name = 'AuthenticationError';
  }
}

export class AuthorizationError extends AppError {
  constructor(message: string = 'Insufficient permissions', details?: unknown) {
    super(message, 403, 'INSUFFICIENT_PERMISSIONS', true, details);
    this.name = 'AuthorizationError';
  }
}

export class ValidationError extends AppError {
  constructor(message: string = 'Validation failed', details?: unknown) {
    super(message, 400, 'VALIDATION_ERROR', true, details);
    this.name = 'ValidationError';
  }
}

export class NotFoundError extends AppError {
  constructor(message: string = 'Resource not found', details?: unknown) {
    super(message, 404, 'NOT_FOUND', true, details);
    this.name = 'NotFoundError';
  }
}

export class CSRFError extends AppError {
  constructor(
    message: string = 'CSRF token validation failed',
    details?: unknown
  ) {
    super(message, 403, 'CSRF_INVALID', true, details);
    this.name = 'CSRFError';
  }
}

export class RateLimitError extends AppError {
  constructor(message: string = 'Rate limit exceeded', details?: unknown) {
    super(message, 429, 'RATE_LIMIT_EXCEEDED', true, details);
    this.name = 'RateLimitError';
  }
}

// Error formatting for API responses
export interface ErrorResponse {
  error: string;
  code: string;
  statusCode: number;
  timestamp: string;
  path?: string;
  details?: unknown;
  stack?: string;
}

export function formatError(
  error: Error | AppError,
  path?: string,
  includeStack: boolean = false
): ErrorResponse {
  const isAppError = error instanceof AppError;

  return {
    error: error.message || 'An unexpected error occurred',
    code: isAppError ? error.code : 'INTERNAL_ERROR',
    statusCode: isAppError ? error.statusCode : 500,
    timestamp: new Date().toISOString(),
    path,
    details: isAppError ? error.details : undefined,
    ...(includeStack &&
      process.env.NODE_ENV === 'development' && { stack: error.stack })
  };
}

// Error logging utility
export function logError(
  error: Error | AppError,
  context?: Record<string, unknown>
): void {
  const errorInfo: Record<string, unknown> = {
    name: error.name,
    message: error.message,
    stack: error.stack,
    ...context
  };

  if (error instanceof AppError) {
    errorInfo.code = error.code;
    errorInfo.statusCode = error.statusCode;
    errorInfo.isOperational = error.isOperational;
    errorInfo.details = error.details;
  }

  if (process.env.NODE_ENV === 'development') {
    // Console logging is handled by the caller or specialized logger
  } else {
    // In production, you might want to send to external logging service
  }
}

// Async error wrapper for API handlers
export function asyncHandler<T extends unknown[], R>(
  fn: (..._args: T) => Promise<R>
) {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args);
    } catch (error) {
      logError(error as Error, {
        handler: fn.name,
        args: args.length > 0 ? 'present' : 'none'
      });
      throw error;
    }
  };
}

// React error boundary hook
export function useErrorHandler() {
  return (error: Error, errorInfo?: { componentStack: string }) => {
    logError(error, {
      type: 'react_error',
      componentStack: errorInfo?.componentStack
    });

    // You can also send to error reporting service here
    if (
      typeof window !== 'undefined' &&
      (window as { gtag?: (..._args: any[]) => void }).gtag
    ) {
      (window as { gtag?: (..._args: any[]) => void }).gtag!(
        'event',
        'exception',
        {
          description: error.message,
          fatal: false
        }
      );
    }
  };
}

// Client-side error handler for fetch requests
export async function handleFetchError(response: Response): Promise<never> {
  let errorData;
  let responseText = '';
  const contentType = response.headers.get('content-type') || '';

  try {
    if (contentType && contentType.includes('json')) {
      errorData = await response.json();
    } else {
      responseText = await response.text();
      errorData = { error: responseText };
    }
  } catch {
    errorData = { error: 'Failed to parse error response' };
  }

  const isGatewayHtml =
    !contentType.includes('json') &&
    (/<\s*html\b/i.test(responseText) ||
      /<\s*!doctype\s+html/i.test(responseText));
  const locale =
    typeof window === 'undefined'
      ? 'en'
      : window.location.pathname.split('/')[1];
  // Bodies shaped {message, messageEn, messageAr} (SapUserFacingException, the
  // PR SAP-lock refusals): show the Arabic text in the Arabic UI. Only the
  // AppError message is localized; the body (AppError.details) stays as sent.
  const arabicMessage =
    locale === 'ar' &&
    typeof errorData === 'object' &&
    errorData !== null &&
    typeof errorData.messageAr === 'string' &&
    errorData.messageAr.trim()
      ? errorData.messageAr
      : null;
  const message = arabicMessage
    ? arabicMessage
    : isGatewayHtml
      ? locale === 'ar'
        ? 'تعذر إكمال الطلب. حدّث الصفحة وتحقق من حالته قبل المحاولة مجدداً.'
        : 'The server could not complete this request. Refresh the page and check its status before trying again.'
      : typeof errorData === 'string'
        ? errorData
        : errorData?.message ||
          errorData?.error ||
          response.statusText ||
          'Request failed';

  const error = new AppError(
    message,
    response.status,
    isGatewayHtml
      ? 'UPSTREAM_GATEWAY_ERROR'
      : typeof errorData === 'object' && errorData !== null
        ? errorData.code || 'HTTP_ERROR'
        : 'HTTP_ERROR',
    true,
    errorData
  );

  throw error;
}

// Retry mechanism for failed operations
export async function withRetry<T>(
  operation: () => Promise<T>,
  maxAttempts: number = 3,
  delay: number = 1000,
  backoff: number = 2
): Promise<T> {
  let lastError: Error;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;

      // Don't retry operational errors that shouldn't be retried
      if (error instanceof AppError && error.isOperational) {
        if ([400, 401, 403, 404, 422].includes(error.statusCode)) {
          throw error;
        }
      }

      if (attempt === maxAttempts) {
        break;
      }

      const waitTime = delay * Math.pow(backoff, attempt - 1);
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }

  throw lastError!;
}

// Type guard for AppError
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}
